/**
 * @fileoverview Hook para sincronização otimizada em dispositivos móveis.
 * Gerencia reconexão automática e fallback para localStorage em mobile.
 * 
 * @module useMobileSync
 * @since 1.0.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile, visibilityManager, mobileConfig } from '../utils/mobileUtils.js';
import { isMobileDevice } from '../utils/deviceDetection.js';
import { mobilePersistence } from '../utils/mobilePersistence.js';
import { loadFromLocalStorage as loadCartFromLocal } from '../store/slices/cartSlice.js';
import { loadFavoritesAsync, loadFromLocalStorage as loadFavoritesFromLocal } from '../store/slices/favoritesSlice.js';
import { loadCartAsync } from '../store/slices/cartSlice.js';

/**
 * Hook para sincronização otimizada em dispositivos móveis.
 * Implementa reconexão automática e carregamento de dados locais.
 * 
 * @returns {Object} Funções de sincronização e estado
 */
export const useMobileSync = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { needsSync: cartNeedsSync } = useSelector((state) => state.cart);
  const { needsSync: favoritesNeedsSync } = useSelector((state) => state.favorites);
  
  const reconnectAttempts = useRef(0);
  const syncTimeout = useRef(null);
  const isInitialized = useRef(false);

  /**
   * Carrega dados locais na inicialização (todas as plataformas).
   */
  const loadLocalData = useCallback(() => {
    if (!isInitialized.current) {
      // Carrega dados locais primeiro para experiência imediata
      if (mobilePersistence.hasLocalData()) {
        dispatch(loadCartFromLocal());
        dispatch(loadFavoritesFromLocal());
      }
      isInitialized.current = true;
    }
  }, [dispatch]);

  /**
   * Tenta sincronizar com servidor com retry automático.
   */
  const syncWithServer = useCallback(async () => {
    if (!isAuthenticated || !user?.email) return;

    try {
      // Tenta carregar dados do servidor
      await Promise.all([
        dispatch(loadFavoritesAsync()).unwrap(),
        dispatch(loadCartAsync()).unwrap()
      ]);
      
      // Reset contador de tentativas em caso de sucesso
      reconnectAttempts.current = 0;
    } catch (error) {
      console.warn('Erro na sincronização, usando dados locais:', error);
      
      // Implementa retry com backoff exponencial
      if (reconnectAttempts.current < mobileConfig.maxRetries) {
        const delay = mobileConfig.retryInterval * Math.pow(2, reconnectAttempts.current);
        reconnectAttempts.current++;
        
        syncTimeout.current = setTimeout(() => {
          syncWithServer();
        }, delay);
      }
    }
  }, [dispatch, isAuthenticated, user?.email]);

  /**
   * Gerencia mudanças de visibilidade da página (todas as plataformas).
   */
  const handleVisibilityChange = useCallback((visibilityState) => {
    if (visibilityState === 'visible' && isAuthenticated) {
      // Página voltou a ficar visível, tenta sincronizar
      syncWithServer();
    } else if (visibilityState === 'hidden') {
      // Página ficou oculta, cancela tentativas de reconexão
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
        syncTimeout.current = null;
      }
    }
  }, [isAuthenticated, syncWithServer]);

  /**
   * Força sincronização manual.
   */
  const forceSync = useCallback(() => {
    reconnectAttempts.current = 0;
    syncWithServer();
  }, [syncWithServer]);

  // Carrega dados locais na inicialização
  useEffect(() => {
    loadLocalData();
  }, [loadLocalData]);

  // Sincroniza quando usuário faz login
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      syncWithServer();
    }
  }, [isAuthenticated, user?.email, syncWithServer]);

  // Gerencia visibilidade da página em todas as plataformas
  useEffect(() => {
    visibilityManager.addListener(handleVisibilityChange);
    return () => {
      visibilityManager.removeListener(handleVisibilityChange);
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
      }
    };
  }, [handleVisibilityChange]);

  // Sincroniza automaticamente quando há mudanças pendentes
  useEffect(() => {
    if (isAuthenticated && (cartNeedsSync || favoritesNeedsSync)) {
      // Debounce para evitar muitas chamadas
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
      }
      
      syncTimeout.current = setTimeout(() => {
        syncWithServer();
      }, 1000);
    }
  }, [isAuthenticated, cartNeedsSync, favoritesNeedsSync, syncWithServer]);

  return {
    forceSync,
    isRetrying: reconnectAttempts.current > 0,
    retryCount: reconnectAttempts.current,
    isMobileDevice: isMobileDevice()
  };
};

export default useMobileSync;